import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { getCategoryPreviewSrc } from "@/constants/catalog-ui";
import { useCatalog } from "@/hooks/use-catalog";
import { catalogHref } from "@/lib/catalog-paths";
import type { CatalogNode } from "@/lib/catalog-tree";
import { cn } from "@/lib/utils";

/**
 * Deeper levels stay reachable from the category page and the sidebar; five columns of
 * ~250px would run off the viewport.
 */
const MAX_DROPDOWN_COLUMNS = 3;

const panelClass =
  "absolute top-full left-0 mt-0 bg-card border border-border rounded-lg shadow-lifted z-50";

interface Column {
  nodes: CatalogNode[];
  /** Slugs of the ancestors above this column, from the hover path. */
  parentSlugs: string[];
  /** The node whose children this column lists; absent for the root column. */
  parent?: CatalogNode;
}

/**
 * Columns for the current hover path: column 0 is the roots, column i the children of
 * whichever node is hovered in column i-1.
 */
function buildColumns(roots: CatalogNode[], hoverPath: string[]): Column[] {
  const columns: Column[] = [{ nodes: roots, parentSlugs: [] }];
  let level = roots;

  for (const [index, slug] of hoverPath.entries()) {
    const node = level.find((candidate) => candidate.id === slug);
    if (!node || node.children.length === 0) break;
    if (columns.length >= MAX_DROPDOWN_COLUMNS) break;
    columns.push({
      nodes: node.children,
      parentSlugs: hoverPath.slice(0, index + 1),
      parent: node,
    });
    level = node.children;
  }

  return columns;
}

interface ColumnProps {
  column: Column;
  /** Id of the node hovered in this column, if any. */
  hoveredId?: string;
  onHover: (path: string[]) => void;
}

/** Leftmost column: the root categories, with thumbnails and no product counts. */
function RootColumn({ column, hoveredId, onHover }: ColumnProps) {
  return (
    <div className="w-[240px] shrink-0 bg-secondary/50 border-r border-border py-3">
      {column.nodes.map((node) => (
        <Link
          key={node.id}
          to={catalogHref([node.id])}
          onMouseEnter={() => onHover([node.id])}
          className={cn(
            "flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors",
            hoveredId === node.id ? "bg-card text-primary" : "text-foreground hover:bg-card/50",
          )}
        >
          <img
            src={getCategoryPreviewSrc(node)}
            alt={node.name}
            loading="lazy"
            className="size-10 object-cover rounded-sm"
          />
          <span className="text-sm font-medium flex-1">{node.name}</span>
          {node.children.length > 0 && (
            <ChevronRight className="size-4 text-muted-foreground shrink-0" />
          )}
        </Link>
      ))}
    </div>
  );
}

/** Any deeper column: text rows with a leaf product count, plus a link to the parent. */
function ChildColumn({ column, hoveredId, onHover }: ColumnProps) {
  const { nodes, parentSlugs, parent } = column;

  return (
    <div className="w-[260px] shrink-0 py-3 bg-card max-h-[400px] overflow-y-auto border-r border-border last:border-r-0">
      {nodes.map((node) => (
        <Link
          key={node.id}
          to={catalogHref([...parentSlugs, node.id])}
          onMouseEnter={() => onHover([...parentSlugs, node.id])}
          className={cn(
            "flex items-center gap-2 px-4 py-3 text-sm transition-colors",
            hoveredId === node.id
              ? "text-primary bg-secondary/30"
              : "text-foreground hover:text-primary hover:bg-secondary/30",
          )}
        >
          <span className="flex-1">{node.name}</span>
          {node.children.length > 0 ? (
            <ChevronRight className="size-4 text-muted-foreground shrink-0" />
          ) : (
            <span className="text-muted-foreground">({node.totalProductCount})</span>
          )}
        </Link>
      ))}

      {parent && (
        <div className="border-t border-border mt-3 pt-3 px-4">
          <Link
            to={catalogHref(parentSlugs)}
            className="text-sm text-primary hover:text-primary/80 font-medium"
          >
            Все товары категории ({parent.totalProductCount}) →
          </Link>
        </div>
      )}
    </div>
  );
}

export function CatalogDropdown() {
  const { data, isLoading, isError } = useCatalog();
  const categories = data?.categories ?? [];
  const [hoverPath, setHoverPath] = useState<string[]>([]);

  if (isLoading) {
    return (
      <div className={`${panelClass} p-6 text-sm text-muted-foreground min-w-[240px]`}>
        Загрузка каталога…
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`${panelClass} p-6 text-sm text-destructive min-w-[240px]`}>
        Каталог временно недоступен
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className={`${panelClass} p-6 text-sm text-muted-foreground min-w-[240px]`}>
        Категории не найдены
      </div>
    );
  }

  // Default to the first root so the second column is never empty on open.
  const effectiveHoverPath = hoverPath.length > 0 ? hoverPath : [categories[0].id];
  const columns = buildColumns(categories, effectiveHoverPath);

  return (
    <div className={`${panelClass} flex overflow-hidden max-w-[calc(100vw-2rem)] overflow-x-auto`}>
      {columns.map((column, columnIndex) => {
        const props = {
          column,
          hoveredId: effectiveHoverPath[columnIndex],
          onHover: setHoverPath,
        };
        const key = column.parentSlugs.join("/") || "root";

        return columnIndex === 0 ? (
          <RootColumn key={key} {...props} />
        ) : (
          <ChildColumn key={key} {...props} />
        );
      })}
    </div>
  );
}
